// @ts-check
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import adminWholesaleService from '../../services/adminWholesaleService';
import {
  formatDay,
  formatRupees,
  label,
  termsSummary,
  WHOLESALE_EXCLUSIVITY,
  WHOLESALE_MARKDOWN_RIGHTS,
  WHOLESALE_MODEL,
} from '../../utils/wholesale';
import Modal from '../ui/Modal';
import { card, EnumSelect, Field, input, primaryBtn, secondaryBtn } from './shared';

const FEE_FIELDS = [
  ['listing', 'Listing fee'],
  ['rail', 'Rail fee'],
  ['marketing', 'Marketing co-pay'],
  ['event', 'Event fee'],
  ['per_order', 'Fee per order'],
  ['expected_pieces', 'Expected pieces in window'],
];

const toForm = (q) => ({
  model: q?.model || 'sample_rail',
  received_at: q?.received_at || '',
  store_share_pct: q?.store_share_pct ?? '',
  wholesale_pct_of_tag: q?.wholesale_pct_of_tag ?? '',
  payment_days: q?.payment_days ?? 30,
  window_weeks: q?.window_weeks ?? '',
  exclusivity: q?.exclusivity || 'none',
  exclusivity_minimum: q?.exclusivity_minimum ?? '',
  markdown_rights: q?.markdown_rights || 'none',
  returns_policy: q?.returns_policy || '',
  reorder_lead_days_required: q?.reorder_lead_days_required ?? '',
  notes: q?.notes || '',
  fees: Object.fromEntries(FEE_FIELDS.map(([k]) => [k, q?.fees?.[k] ?? ''])),
});

const num = (v) => (v === '' || v === null ? null : Number(v));

/** @param {{partnerId: string, quote?: any, onClose: () => void, onSaved: () => void}} props */
const QuoteModal = ({ partnerId, quote, onClose, onSaved }) => {
  const toast = useToast();
  const [form, setForm] = useState(() => toForm(quote));
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const outright = form.model === 'outright';

  const save = async () => {
    const body = {
      model: form.model,
      received_at: form.received_at || null,
      store_share_pct: outright ? null : num(form.store_share_pct),
      wholesale_pct_of_tag: outright ? num(form.wholesale_pct_of_tag) : null,
      payment_days: Number(form.payment_days),
      window_weeks: num(form.window_weeks),
      exclusivity: form.exclusivity,
      exclusivity_minimum: num(form.exclusivity_minimum),
      markdown_rights: form.markdown_rights,
      returns_policy: form.returns_policy.trim() || null,
      reorder_lead_days_required: num(form.reorder_lead_days_required),
      notes: form.notes.trim() || null,
      fees: Object.fromEntries(Object.entries(form.fees).filter(([, v]) => v !== '')),
    };
    if (outright && body.wholesale_pct_of_tag === null) return toast.error('Enter the percentage of the GST-exclusive tag');
    if (!outright && body.store_share_pct === null) return toast.error("Enter the store's share");
    setSaving(true);
    try {
      if (quote) await adminWholesaleService.updateQuote(quote.id, body);
      else await adminWholesaleService.addQuote(partnerId, body);
      toast.success(quote ? 'Quote updated' : 'Quote saved');
      onSaved();
    } catch (err) {
      toast.error(err.message || 'Failed to save the quote');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal maxWidth="2xl" onClose={onClose}>
      <div className="px-6 py-4 border-b">
        <h2 className="text-lg font-semibold">{quote ? (quote.is_counter ? 'Edit our counter' : 'Edit quote') : 'Log a quote'}</h2>
      </div>
      <div className="px-6 py-4 space-y-3 max-h-[65vh] overflow-y-auto">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Model">
            <EnumSelect value={form.model} onChange={(model) => set({ model, payment_days: model === 'outright' ? 0 : 30 })} options={Object.values(WHOLESALE_MODEL)} />
          </Field>
          {outright ? (
            <Field label="% of GST-exclusive tag *">
              <input type="number" min="0" max="100" step="0.5" className={input} value={form.wholesale_pct_of_tag} onChange={(e) => set({ wholesale_pct_of_tag: e.target.value })} />
            </Field>
          ) : (
            <Field label="Store share % *">
              <input type="number" min="0" max="100" step="0.5" className={input} value={form.store_share_pct} onChange={(e) => set({ store_share_pct: e.target.value })} />
            </Field>
          )}
          <Field label={outright ? 'Payment days (0 = on delivery)' : 'Payment days from sale'}>
            <input type="number" min="0" max="365" className={input} value={form.payment_days} onChange={(e) => set({ payment_days: e.target.value })} />
          </Field>
          <Field label="Window (weeks)">
            <input type="number" min="0" className={input} value={form.window_weeks} onChange={(e) => set({ window_weeks: e.target.value })} />
          </Field>
          <Field label="Exclusivity">
            <EnumSelect value={form.exclusivity} onChange={(exclusivity) => set({ exclusivity })} options={Object.values(WHOLESALE_EXCLUSIVITY)} />
          </Field>
          {form.exclusivity !== 'none' && (
            <Field label="Minimum purchase demanded for it (₹)">
              <input type="number" min="0" className={input} value={form.exclusivity_minimum} onChange={(e) => set({ exclusivity_minimum: e.target.value })} />
            </Field>
          )}
          <Field label="Markdown rights" hint="Unilateral takes the quote out of the ranking">
            <EnumSelect value={form.markdown_rights} onChange={(markdown_rights) => set({ markdown_rights })} options={Object.values(WHOLESALE_MARKDOWN_RIGHTS)} />
          </Field>
          <Field label="They expect reorders in (days)">
            <input type="number" min="0" className={input} value={form.reorder_lead_days_required} onChange={(e) => set({ reorder_lead_days_required: e.target.value })} />
          </Field>
          <Field label="Received on" hint="Today if left empty">
            <input type="date" className={input} value={form.received_at} onChange={(e) => set({ received_at: e.target.value })} />
          </Field>
        </div>
        <div>
          <p className="text-sm font-medium mb-1">Fees (₹)</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {FEE_FIELDS.map(([key, text]) => (
              <Field key={key} label={text}>
                <input type="number" min="0" className={input} value={form.fees[key]} onChange={(e) => set({ fees: { ...form.fees, [key]: e.target.value } })} />
              </Field>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-1">Listing, rail, marketing and event fees are spread over the expected pieces and taken off the net.</p>
        </div>
        <Field label="Returns policy for unsold pieces">
          <input className={input} value={form.returns_policy} onChange={(e) => set({ returns_policy: e.target.value })} />
        </Field>
        <Field label="Notes">
          <textarea className={input} rows={2} value={form.notes} onChange={(e) => set({ notes: e.target.value })} />
        </Field>
      </div>
      <div className="flex gap-2 px-6 py-4 border-t">
        <button onClick={save} disabled={saving} className={primaryBtn}>
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button onClick={onClose} disabled={saving} className={secondaryBtn}>
          Cancel
        </button>
      </div>
    </Modal>
  );
};

const STATUS_STYLE = {
  open: 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300',
  accepted: 'bg-green-100 text-green-800 dark:bg-green-950/60 dark:text-green-300',
  superseded: 'bg-muted text-muted-foreground',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300',
};

/** @param {{partner: any, onChanged: () => void}} props */
const QuotesTab = ({ partner, onChanged }) => {
  const toast = useToast();
  const [quotes, setQuotes] = useState([]);
  const [modal, setModal] = useState(undefined); // undefined closed, null new, object editing

  const load = useCallback(async () => {
    try {
      setQuotes(await adminWholesaleService.listQuotes(partner.id));
    } catch (err) {
      toast.error(err.message || 'Failed to load the quotes');
    }
  }, [partner.id, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn, done) => {
    try {
      const result = await fn();
      toast.success(done);
      await load();
      onChanged();
      return result;
    } catch (err) {
      toast.error(err.message || 'Failed');
      return null;
    }
  };

  const counter = async (q) => {
    const created = await act(() => adminWholesaleService.counterQuote(q.id), 'Counter created from their terms. Change what you are countering');
    if (created) setModal(created);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold">Quotes</h2>
        <button onClick={() => setModal(null)} className={primaryBtn}>
          Log a quote
        </button>
      </div>
      {quotes.length === 0 ? (
        <div className={`${card} p-8 text-center text-sm text-muted-foreground`}>No quotes yet. Log their terms once they send them.</div>
      ) : (
        <div className={`${card} overflow-x-auto`}>
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                {['Received', 'Model', 'Terms', 'Flags', 'Net / ₹19,500 piece', 'Status', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {quotes.map((q) => (
                <tr key={q.id}>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">{formatDay(q.received_at)}</td>
                  <td className="px-4 py-3 text-sm">
                    {label(q.model)}
                    {q.is_counter ? <span className="block text-xs text-muted-foreground">our counter</span> : null}
                  </td>
                  <td className="px-4 py-3 text-sm">{termsSummary(q)}</td>
                  <td className="px-4 py-3 text-sm">
                    {q.markdown_rights === 'unilateral' && <span className="text-red-700 dark:text-red-400">Unilateral markdowns</span>}
                    {Object.keys(q.fees || {}).length > 0 && <span className="block text-xs text-muted-foreground">Fees: {Object.entries(q.fees).map(([k, v]) => `${k} ${v}`).join(', ')}</span>}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold">{formatRupees(q.net_realisation_per_piece)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-md text-xs font-medium ${STATUS_STYLE[q.status]}`}>{label(q.status)}</span>
                  </td>
                  <td className="px-4 py-3">
                    {q.status === 'open' && (
                      <div className="flex gap-2 whitespace-nowrap">
                        <button onClick={() => setModal(q)} className="text-sm underline">Edit</button>
                        <button onClick={() => counter(q)} className="text-sm underline">Counter</button>
                        <button onClick={() => act(() => adminWholesaleService.acceptQuote(q.id), 'Quote accepted')} className="text-sm underline">Accept</button>
                        <button onClick={() => act(() => adminWholesaleService.rejectQuote(q.id), 'Quote rejected')} className="text-sm underline text-red-700 dark:text-red-400">Reject</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {modal !== undefined && (
        <QuoteModal
          partnerId={partner.id}
          quote={modal || undefined}
          onClose={() => setModal(undefined)}
          onSaved={() => {
            setModal(undefined);
            load();
            onChanged();
          }}
        />
      )}
    </div>
  );
};

export default QuotesTab;
